import {Menu, Step, Action} from '../../../../../shared/models';

import {
  actionHeight,
  endStepValue,
  flowHeight,
  flowStepType,
  horizontOfset,
  menuHeight,
  menuType,
  newLine,
  newStepDescription,
  newStepHeight,
  newStepValue,
  verticalOfset
} from '../utils/constants';
import { FlowStepTypes, MenuTypes } from '../utils/flow-step-types.enum';
import {
  DeleteNodeItem,
  DeleteStepData,
  DeleteTreeNode,
  FlowBuilder,
  FlowDetails,
  NextStepDetails,
  NodeDetails,
  OperationState,
} from '../models';
import { JsPlumbService } from '../js-plumb.service';
import { FlowBuilderService } from '../flow-builder.service';
import { Injectable, OnDestroy } from "@angular/core";
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import find from 'lodash/find';
import remove from 'lodash/remove';
import { Store } from '@ngrx/store';
import { selectNodeTypesMap } from '../../../../../core/state/selectors/flow.selector';
import { take, tap } from "rxjs/operators";

import map from 'lodash/map';
import { filter as _filter } from 'lodash';
import { Observable, of, Subscription } from "rxjs";
import {Canvas, NodeObject} from "../../../../../shared/utils/nodeTypes";
import { DomSanitizer } from '@angular/platform-browser';

// !!!NOTE!!! Adding a new menu.... make sure that you add it to getMenuDetails(...) and make sure you update setMenuNext(...)
@Injectable({
  providedIn: 'root',
})
export class FlowBuilderImplService implements FlowBuilderService, OnDestroy {
  private area: HTMLDivElement;
  private flowDetails: FlowDetails[] = [];
  private horizontalPosition: number;
  private verticalPosition: number;
  private currentDisplayedSteps: string[] = [];
  private currentDeletedStepsId: string[] = [];
  private currentDeletedSteps: FlowDetails[] = [];
  private jsPlumbService: JsPlumbService;
  private variableList: Object = {};

  private parentDeletedNodes: string[] = [];
  private childDeletedNodes: any[] = [];
  private deleteNodesTree: any[] = [];
  private deleteTreeNode: DeleteTreeNode[] = [];
  private currentDeletedStepsToTree: string[] = [];
  private currentCountedChildrenSteps: string[] = [];
  private deletedParent: DeleteNodeItem[] = [];
  private childCount = 0;

  private flow: any;
  private flowStatus: any;
  operationData: OperationState;
  copiedStep: Step;
  private loops = [];
  currFlow: string;
  modalOpen: boolean = false;

  private nodeInfo;
  private nodeTypeSubscription: Subscription;

  constructor(private store: Store, private sanitizer: DomSanitizer) {
    this.nodeTypeSubscription = this.store.select(selectNodeTypesMap).subscribe(node => this.nodeInfo = node);
  }

  ngOnDestroy() {
    this.nodeTypeSubscription.unsubscribe();
  }

  public set setFlowArea(area: HTMLDivElement) {
    this.area = area;
    this.jsPlumbService = new JsPlumbService(area, this.sanitizer);
  }

  public get getFlowToDraw(): Observable<FlowDetails[]> {
    if (!this.flowDetails.length) {
      return of();
    }
    return of(this.flowDetails);
  }

  public get getDeletedSteps(): DeleteTreeNode[] {
    return this.deleteTreeNode;
  }

  public get getOperationState(): OperationState {
    if (this.operationData && this.operationData.id) {
      return this.operationData;
    }
    return null;
  }

  public get getVariables(): string[] {
    return (this.variableList = Object.keys(this.variableList)
      .sort(alphabeticallyOrderMap)
      .map((variableListKey) => {
        return this.variableList[variableListKey];
      }));
  }

  public get getAllSteps(): string[] {
    return sortBy(this.currentDeletedStepsId.concat(this.currentDisplayedSteps), (o) =>
      o === 'start' ? 0 : +o.replace('step', '')
    );
  }

  public get getAllStepsOnCanvas(): string[] {
    return this.currentDisplayedSteps;
  }

  public get getAllStepsInTrash(): string[] {
    return this.currentDeletedStepsId;
  }

  public get getAllStepsDetails(): any {
    const sort = sortBy(this.currentDeletedStepsId.concat(this.currentDisplayedSteps), (o) =>
      o === 'start' ? 0 : +o.replace('step', '')
    );
    const nodeDetailMap = {};
    for (const step of sort) {
      const details = this.getNodeDetails(this.flow[step], step).nodeDetails;
      nodeDetailMap[details.nodeId] = details;
    }
    return nodeDetailMap;
  }

  public getStep(stepId): any {
    return this.flow[stepId];
  }

  public buildFlow(flow: any, flowStatus?: any): any {
    this.resetFlow();
    this.flowBuilder(flow, flowStatus).then(() => {
      this.deletedSteps(flow, this.currentDisplayedSteps);
      setTimeout(() => {
        if (this.jsPlumbService) {
          this.jsPlumbService.addJsPlumbArrows();
          this.jsPlumbService.addJsPlumbClick();
        }
      }, 0);
    });
  }

  public newFlow(): void {
    this.buildFlow({})
  }

  private rebuildFlow() {
    this.buildFlow(this.flow)
  }

  public resetFlow() {
    this.flowDetails = [];
    this.currentDeletedSteps = [];
    if (this.jsPlumbService) {
      this.jsPlumbService.resetConnections();
    }
    this.variableList = {};
    this.currentDisplayedSteps = [];
    this.currentDeletedStepsId = [];
    this.deleteTreeNode = [];
    this.currentDeletedStepsToTree = [];
    this.currentCountedChildrenSteps = [];
    this.parentDeletedNodes = [];
    this.childDeletedNodes = [];
    this.deleteNodesTree = [];
    this.deletedParent = [];
  }

  private async flowBuilder(flow: any, flowStatus?: any): Promise<void> {
    this.flow = flow;
    this.flowStatus = flowStatus;
    this.horizontalPosition = 0;
    this.verticalPosition = 0;

    this.currentDisplayedSteps = [];

    const startOfFlow = this.setupStartNode(flow);
    await this.nextStepCalculator(
      startOfFlow.flowBuilder,
      this.horizontalPosition,
      startOfFlow.firstStep
    );
  }

  setNodeValid(node, valid) {
    this.flow[node].valid = valid;
  }

  checkNodeValid(node) {
    if (this.flow[node] && this.flow[node].hasOwnProperty('valid')) {
      return this.flow[node].valid;
    }
    return true;
  }

  setupStartNode(flow): any {
    const firstStep = 'start';
    let step = flow[firstStep];

    if (!step) {
      this.flow['start'] = this.startNodeConfig();
      step = this.flow['start'];
    }
    this.addVariable(step);
    const flowBuilder = this.getNodeDetails(step, firstStep);
    this.addStepToCurrentDisplayed(firstStep);
    this.addNode(flowBuilder.nodeDetails, this.horizontalPosition, firstStep);
    return {
      flowBuilder: flowBuilder,
      firstStep: firstStep,
    };
  }

  private deletedSteps(flow: any, currentDisplayedSteps: string[]) {
    const deletedSteps = calculatorDeletedSteps(flow, currentDisplayedSteps);
    if (deletedSteps) {
      for (const deletedStepId in deletedSteps) {
        const nodeDetails: NodeDetails = this.getNodeDetails(
            deletedSteps[deletedStepId],
            deletedStepId
        )
            ? this.getNodeDetails(deletedSteps[deletedStepId], deletedStepId).nodeDetails
            : null;

        if (nodeDetails) {
          const stepNumber = getStepNumber(nodeDetails.nodeId);
          this.currentDeletedStepsId.push(stepNumber);
          this.currentDeletedSteps.push(
              new FlowDetails(
                  nodeDetails.description,
                  nodeDetails.stepType,
                  nodeDetails.nodeType,
                  stepNumber,
                  nodeDetails.nodeId,
                  0,
                  0,
                  '',
                  nodeDetails.canvas,
                  getDisplayType(nodeDetails.stepType),
                  false,
                  nodeDetails.title
              )
          );
        }
      }
    }
    if (this.currentDeletedStepsId.length > 0) {
      // tslint:disable-next-line:no-shadowed-variable
      for (const deletedSteps of this.currentDeletedStepsId) {
        if (!this.currentDeletedStepsToTree.includes(deletedSteps)) {
          const firstStep: FlowBuilder = this.getNodeDetails(this.flow[deletedSteps], deletedSteps);
          this.currentDeletedStepsToTree.push(deletedSteps);
          this.addDeletedNode(firstStep, 0, deletedSteps);

          this.BuildDeleteTreeNode(firstStep, 0, deletedSteps);
        }
      }
    }
  }

  private BuildDeleteTreeNode(
    flowBuilder: FlowBuilder,
    position: number,
    currentStepId: string
  ): void {
    position += 1;
    remove(this.loops, (n) => n === currentStepId);
    if (flowBuilder.nextSteps) {
      for (const nextStep of flowBuilder.nextSteps) {
        if (nextStep && nextStep.nextStepId !== '--END--' && nextStep.nextStepId !== newStepValue) {
          if (nextStep.nextStepId !== currentStepId && isFlowId(nextStep.nextStepId)) {
            const newStep = this.flow[nextStep.nextStepId];
            const stepDetails: FlowBuilder = this.getNodeDetails(newStep, nextStep.nextStepId);
            if (!this.parentDeletedNodes.includes(nextStep.nextStepId)) {
              this.parentDeletedNodes.push(currentStepId);
            }
            if (!this.parentDeletedNodes.includes(currentStepId)) {
              this.addDeletedNewStep(nextStep, currentStepId, stepDetails, position);
            } else {
              this.addDeletedExistingStep(stepDetails, currentStepId, nextStep, position);
            }
          } else if (nextStep.nextStepId === currentStepId) {
          } else if (!isFlowId(nextStep.nextStepId)) {
            // this.addDeletedFlowNode(currentStepId, nextStep, position);
          }
        }
      }
    }
  }

  getChildrenCount(stepId: string): number {
    this.childCount = 0;
    const firstStep: FlowBuilder = this.getNodeDetails(this.flow[stepId], stepId);
    this.currentCountedChildrenSteps = [];
    this.getChildCount(firstStep, stepId);
    return this.childCount;
  }

  private getChildCount(flowBuilder: FlowBuilder, currentStepId: string): void {
    remove(this.loops, (n) => n === currentStepId);
    if (flowBuilder.nextSteps) {
      for (const nextStep of flowBuilder.nextSteps) {
        if (nextStep && nextStep.nextStepId !== '--END--' && nextStep.nextStepId !== newStepValue) {
          if (nextStep.nextStepId !== currentStepId && isFlowId(nextStep.nextStepId)) {
            const newStep = this.flow[nextStep.nextStepId];
            const stepDetails: FlowBuilder = this.getNodeDetails(newStep, nextStep.nextStepId);
            if (stepDetails.nodeDetails.nodeType !== 'new') {
              if (!this.currentCountedChildrenSteps.includes(nextStep.nextStepId)) {
                this.childCount++;
                this.currentCountedChildrenSteps.push(nextStep.nextStepId);
                this.getChildCount(stepDetails, nextStep.nextStepId);
              } else {
                this.childCount++;
              }
            }
          } else if (nextStep.nextStepId === currentStepId) {
            this.childCount++;
          } else if (!isFlowId(nextStep.nextStepId)) {
            this.childCount++;
          }
        }
      }
    }
  }

  private nextStepCalculator(
    flowBuilder: FlowBuilder,
    horizontalPosition: number,
    currentStepId: string
  ) {
    horizontalPosition += horizontOfset;
    remove(this.loops, (n) => n === currentStepId);
    if (flowBuilder.nextSteps) {
      for (const nextStep of flowBuilder.nextSteps) {
        if (nextStep && nextStep.nextStepId !== '--END--' && nextStep.nextStepId !== newStepValue) {
          if (nextStep.nextStepId !== currentStepId && isFlowId(nextStep.nextStepId)) {
            const newStep = this.flow[nextStep.nextStepId];
            this.addVariable(newStep);
            const stepDetails: FlowBuilder = this.getNodeDetails(newStep, nextStep.nextStepId);
            if (!this.currentDisplayedSteps.includes(nextStep.nextStepId)) {
              this.addNewStep(nextStep, currentStepId, stepDetails, horizontalPosition);
            } else {
              this.addExistingStep(stepDetails, currentStepId, nextStep, horizontalPosition);
            }
          } else if (nextStep.nextStepId === currentStepId) {
            this.loops.push(currentStepId);
            // this.jsPlumbService.JsPlumbLoop(currentStepId);
          } else if (!isFlowId(nextStep.nextStepId) && !isStartStep(nextStep.nextStepId)) {
            this.addFlowNode(currentStepId, nextStep, horizontalPosition);
          } else if (isStartStep(nextStep.nextStepId)) {
            const newStep = this.flow[nextStep.nextStepId];
            const stepDetails: FlowBuilder = this.getNodeDetails(newStep, nextStep.nextStepId);
            stepDetails.nodeDetails.nodeType = 'startExisting';
            this.addExistingStep(stepDetails, currentStepId, nextStep, horizontalPosition);
          }
        }
      }
    }
  }

  private getAnchorType(source, target, existing: string = null) {
    const { flow } = this;
    const nextId: string = existing ? existing : target;

    if (flow && flow[nextId] && flow[nextId].type === 'menu') {
      const flowMenu = flow[nextId].menu;
      const currentMenuType: string = flowMenu.menuType
        ? flowMenu.menuType
        : getMenuType(flow[nextId]);

      if (this.nodeInfo[currentMenuType] && this.nodeInfo[currentMenuType].sortType === 'Input') {
        return 'input';
      }
    }

    return 'default';
  }

  private addFlowNode(currentStepId: string, nextStep, horizontalPosition: number) {
    const nodeId = idGenerator(currentStepId, nextStep.description, nextStep.nextStepId);
    const nodeDetails = new NodeDetails(
      nextStep.nextStepId,
      FlowStepTypes.FLOW,
      FlowStepTypes.FLOW,
      nodeId,
        null,
        ""
    );
    if (this.flowStatus === 'Copied') {
      this.setNodeValid(currentStepId, false);
    }
    const stepDetails: FlowBuilder = new FlowBuilder(nodeDetails, [null]);
    const anchorType = this.getAnchorType(currentStepId, stepDetails.nodeDetails.nodeId);
    if (this.jsPlumbService) {
      this.jsPlumbService.toJsPlumb(
        currentStepId,
        'flow-' + stepDetails.nodeDetails.nodeId,
        nextStep.description,
        anchorType
      );
    }
    this.addNode(stepDetails.nodeDetails, horizontalPosition, currentStepId);
  }

  private addExistingStep(
    stepDetails,
    currentStepId: string,
    nextStep,
    horizontalPosition: number
  ) {
    stepDetails.nodeDetails.nodeId = idGenerator(
      currentStepId,
      nextStep.description,
      nextStep.nextStepId
    );
    const anchorType = this.getAnchorType(
      currentStepId,
      stepDetails.nodeDetails.nodeId,
      nextStep.nextStepId
    );
    if (this.jsPlumbService) {
      this.jsPlumbService.toJsPlumb(
        currentStepId,
        stepDetails.nodeDetails.nodeId,
        nextStep.description,
        anchorType
      );
    }
    this.addNode(stepDetails.nodeDetails, horizontalPosition, currentStepId, true);
  }

  private addNewStep(
    nextStep: NextStepDetails,
    currentStepId: string,
    stepDetails,
    horizontalPosition: number
  ) {
    if (nextStep && stepDetails) {
      this.addStepToCurrentDisplayed(nextStep.nextStepId);
      const anchorType = this.getAnchorType(currentStepId, nextStep.nextStepId);
      if (this.jsPlumbService) {
        this.jsPlumbService.toJsPlumb(
            currentStepId,
            nextStep.nextStepId,
            nextStep.description,
            anchorType
        );
      }
      this.addNode(stepDetails.nodeDetails, horizontalPosition, currentStepId);
      this.nextStepCalculator(stepDetails, horizontalPosition, nextStep.nextStepId);
    }
  }

  private addNode(nodeDetails: NodeDetails, horizontalPosition: number, currentStep: string, isExisting: boolean = false) {
    const stepNumber = getStepNumber(nodeDetails.nodeId);

    if (currentStep === 'start' && stepNumber !== 'start') {
      this.verticalPosition -= 100;
      horizontalPosition -= 80;
    }

    this.flowDetails.push(
      new FlowDetails(
        nodeDetails.description,
        nodeDetails.stepType,
        nodeDetails.nodeType,
        stepNumber,
        nodeDetails.nodeId,
        horizontalPosition,
        this.verticalPosition,
        currentStep,
        nodeDetails.canvas,
        getDisplayType(nodeDetails.stepType),
        isExisting,
        nodeDetails.title
      )
    );

    switch (nodeDetails.stepType) {
      case flowStepType.NEW:
        this.verticalPosition += newStepHeight;
        break;
      case flowStepType.ACTION:
        this.verticalPosition += actionHeight + 50;
        break;
      case flowStepType.MENU:
      case flowStepType.INTEGRATION:
        this.verticalPosition += menuHeight + 50;
        break;
      case flowStepType.START:
      case flowStepType.FLOW:
        this.verticalPosition += flowHeight;
        break;
    }

    this.verticalPosition += verticalOfset;
  }

  private addDeletedFlowNode(currentStepId: string, nextStep, horizontalPosition: number) {
    const stepId = idGenerator(currentStepId, nextStep.description, nextStep.nextStepId);
    const nodeDetails = new NodeDetails(
      nextStep.nextStepId,
      FlowStepTypes.FLOW,
      FlowStepTypes.FLOW,
      'F',
      null,
      ''
    );
    const stepDetails: FlowBuilder = new FlowBuilder(nodeDetails, [null]);
    this.addDeletedNode(stepDetails, horizontalPosition, currentStepId);
  }

  private addDeletedExistingStep(
    stepDetails,
    currentStepId: string,
    nextStep,
    horizontalPosition: number
  ) {
    const filter = this.deletedParent.filter((x) => x.id === nextStep.nextStepId);
    if (filter.length === 1) {
      const index = filter[0].index;
      let count = 0;
      let i = index;
      while (i < this.deleteTreeNode.length) {
        const currentValue = this.deleteTreeNode[i];
        if (currentValue.level !== 0) {
          count++;
        } else {
          break;
        }
        i++;
      }
      this.deleteTreeNode = this.deleteTreeNode.splice(index, count);
    }
    if (!this.currentDeletedStepsToTree.includes(nextStep.nextStepId)) {
      this.addDeletedNewStep(nextStep, currentStepId, stepDetails, horizontalPosition);
    } else {
      stepDetails.nodeDetails.nodeId = idGenerator(
        currentStepId,
        nextStep.description,
        nextStep.nextStepId
      );
      this.addDeletedNode(stepDetails.nodeDetails, horizontalPosition, currentStepId, true);
    }
  }

  private addDeletedNewStep(
    nextStep: NextStepDetails,
    currentStepId: string,
    stepDetails: FlowBuilder,
    horizontalPosition: number
  ) {
    this.addStepToCurrentDisplayed(nextStep.nextStepId)
    this.addDeletedNode(stepDetails, horizontalPosition, currentStepId);
    this.BuildDeleteTreeNode(stepDetails, horizontalPosition, nextStep.nextStepId);
  }

  private addDeletedNode(stepDetails: FlowBuilder, position: number, currentStep: string, isExisting: boolean = false) {
    const nodeDetails = stepDetails.nodeDetails;
    if (nodeDetails && nodeDetails.nodeType !== 'new') {
      const stepNumber = getStepNumber(nodeDetails.nodeId);

      let isExpandable = false;
      if (stepDetails && stepDetails.nextSteps) {
        for (const nextSteps of stepDetails.nextSteps) {
          if (isExpandable) {
            break;
          }
          if (nextSteps && this.flow[nextSteps.nextStepId]) {
            isExpandable = true;
          }
        }
      }

      const deleteTreeNode: DeleteTreeNode = {
        expandable: isExpandable,
        isExpanded: false,
        level: position,
        step: new FlowDetails(
          nodeDetails.description,
          nodeDetails.stepType,
          nodeDetails.nodeType,
          stepNumber,
          nodeDetails.nodeId,
          position,
          this.verticalPosition,
          currentStep,
          nodeDetails.canvas,
          getDisplayType(nodeDetails.stepType),
          isExisting,
          nodeDetails.title
        ),
      };

      this.deleteTreeNode.push(deleteTreeNode);
      if (position === 0) {
        this.deletedParent.push({ id: currentStep, index: this.deleteTreeNode.length - 1 });
        this.deletedParent.filter((x) => x.id === currentStep);
      }
      this.currentDeletedStepsToTree.push(stepNumber);
    }
  }

  private addVariable(step: Step): any {
    if (step) {
      if (step.menu) {
        const { menu } = step;
        if (menu.input) {
          const keyValue = menu.input.varName;
          if (keyValue) {
            this.variableList[keyValue] = '[[' + keyValue + ']]';
          }
        }
      } else if (step.action) {
        const { action } = step;
        const keyValue = action.name;
        if (keyValue) {
          switch (action.actionType) {
            case 'httpCall':
              break
            case 'setVariable':
              const actionVariable = action.actionParams['variables'];
              for (const variable in actionVariable) {
                this.variableList[variable] = `[[${variable}]]`;
              }
              break
            default:
              this.variableList[keyValue] = '[[' + keyValue + ':result' + ']]';
          }
        }
      }
    }
  }

  getNodeDetails(step: Step, stepId: string): FlowBuilder {
    const stepType: FlowStepTypes = getStepType(step);
    switch (stepType) {
      case flowStepType.NEW:
        return this.newNode(stepId, stepType);
      case flowStepType.ACTION:
        return this.getActionDetails(step.action, stepId, stepType);
      case flowStepType.MENU:
        return this.getMenuDetails(step, stepId, stepType);
      case flowStepType.START:
        return this.getStartDetails(step, stepId, stepType);
      case FlowStepTypes.INTEGRATION:
        return this.getIntegration(step, stepId, stepType);
      default:
        console.log('default', stepId);
        return null;
      //    new step;
    }
  }

  setNextStep(step: Step, nextStepData?: OperationState): Object {
    const stepType: FlowStepTypes = getStepType(step);
    switch (stepType) {
      case flowStepType.ACTION:
        return this.setActionNext(step, nextStepData);
      case flowStepType.MENU:
        return this.setMenuNext(step, nextStepData);
      case flowStepType.START:
        return this.setStartNext(step, nextStepData);
      case FlowStepTypes.INTEGRATION:
        return this.setIntegrationNext(step);
      default:
        console.log('default', step);
        break;
      //    new step;
    }
  }

  newNode(stepId: string, stepType: FlowStepTypes): FlowBuilder {
    const nodeDetails = new NodeDetails(newStepDescription, stepType, stepType, stepId, null, '');
    return new FlowBuilder(nodeDetails, [new NextStepDetails('', '--END--')]);
  }

  getActionDetails(action: Action, stepId: string, stepType: FlowStepTypes): FlowBuilder {
    const { description, actionType } = action;
    const nextSteps = Object.keys(action.result).map(
      (result) => new NextStepDetails(result, action.result[result])
    );

    if (this.flowStatus && this.flowStatus === 'Copied' && actionType === 'httpCall') {
      this.setNodeValid(stepId, false);
    }

    if (this.nodeInfo[actionType]) {
      const canvas: Canvas = this.nodeInfo[actionType].canvas;
      const title: string = this.nodeInfo[actionType].title;

      const nodeDetails = new NodeDetails(description, stepType, actionType, stepId, canvas, title);

      return new FlowBuilder(nodeDetails, nextSteps);
    }
  }

  setActionNext(step: Step, nextStepData: OperationState) {
    const actionResult = step.action.result;
    Object.keys(actionResult).map((result) => {
      actionResult[result] = this.setNextStepValue(actionResult[result], nextStepData, result);
    });
    return step;
  }

  getStartDetails(step: Step, stepId: string, stepType: FlowStepTypes): FlowBuilder {
    const description = '';
    const canvas: Canvas = this.nodeInfo['start'].canvas;
    const title: string = this.nodeInfo['start'].title;
    const nodeDetails = new NodeDetails(description, stepType, stepType, stepId, canvas, title);
    const nextSteps = [new NextStepDetails(description, step.next)];

    return new FlowBuilder(nodeDetails, nextSteps);
  }

  getIntegration(step: Step, stepId: string, stepType: FlowStepTypes): FlowBuilder {
    const integration = step.integration;
    const { description, integrationType } = integration;
    const nextSteps: NextStepDetails[] = [];

    let nodeType;
    switch (integrationType) {
      case 'endNode':
        nodeType = integration.endNode.endNodeType;
        switch (nodeType) {
          case "async_api":
            nextSteps.push(new NextStepDetails('next', integration.endNode.outputVars["next"]));
            break;
        }
        break;
      case 'nestedNode':
        nodeType = 'nestedNode';
        nextSteps.push(new NextStepDetails('next', step.next));
        break;
      default:
        console.error('Invalid integration node', step);
        return
    }
    return this.integrationNodeType(this.nodeInfo[nodeType], nodeType, description, stepType, stepId, nextSteps);
  }

  private integrationNodeType(nodeObject: NodeObject, nodeType, description: string, stepType: FlowStepTypes, stepId: string, nextSteps: NextStepDetails[]) {
    const canvas: Canvas = nodeObject.canvas;
    const title: string = nodeObject.title;
    const nodeDetails = new NodeDetails(description, stepType, nodeType, stepId, canvas, title);
    return new FlowBuilder(nodeDetails, nextSteps);
  }


  getMenuDetails(step: Step, stepId: string, stepType: FlowStepTypes): FlowBuilder {
    const menu = step.menu;
    const currentMenuType: string = menu.menuType ? menu.menuType : getMenuType(step);
    let nodeText = '';

    let nextSteps: NextStepDetails[] = [null];

    switch (currentMenuType) {
      // case menuType.DISPLAYMENU:
      case menuType.ASKINPUT:
      case menuType.MEDIA:
        nextSteps = step.next
          ? [new NextStepDetails('input', step.next)]
          : [new NextStepDetails('', '--END--')];
        nodeText = menu.text;
        break;
      case menuType.DISPLAYMENU:
        nextSteps = step.next
          ? [new NextStepDetails('', step.next)]
          : [new NextStepDetails('', '--END--')];
        nodeText = menu.text;
        break;
      case menuType.EXTERNALLINK:
        nextSteps = Object.keys(menu.results).map(
          (result) => new NextStepDetails(result.toString(), menu.results[result].next)
        );
        nodeText = menu.text + newLine + menu.url;
        break;
      case menuType.PAYMENT:
        nextSteps = Object.keys(menu.results).map(
          (result) => new NextStepDetails(result.toString(), menu.results[result].next)
        );
        nodeText = menu.payment.operationType;
        break;
      case menuType.LOCATION:
        nextSteps = step.next
          ? [new NextStepDetails('input', step.next)]
          : [new NextStepDetails('', '--END--')];
        nodeText = menu.text;
        break;
      case menuType.OTPMENU:
        nextSteps = Object.keys(menu.results).map(
          (result) => new NextStepDetails(result.toString(), menu.results[result].next)
        );
        nodeText = menu.text;
        break;
      case menuType.MENUOPTION:
        nextSteps = Object.keys(menu.options.items).map((items) => {
          if (menu.options.items[items].next) {
            return new NextStepDetails(
              items + ':' + menu.options.items[items].text,
              menu.options.items[items].next
            );
          } else if (menu.options.items[items].flowId) {
            return new NextStepDetails(
              items + ':' + menu.options.items[items].text,
              menu.options.items[items].flowId
            );
          }
        });

        if (menu?.results?.default && menu?.results?.default?.next !== 'OPTIONAL') {
          nextSteps.push(new NextStepDetails('default', menu.results.default.next))
        }
        nodeText = this.getMenuOptionDescription(menu, nodeText);
        break;
      case menuType.MULTIPLECHOICE:
        nextSteps = step.next
            ? [new NextStepDetails('input', step.next)]
            : [new NextStepDetails('', '--END--')];

        if (menu?.results && menu?.results["default"]?.next && menu?.results["default"]?.next !== 'OPTIONAL') {
          nextSteps.push(new NextStepDetails('default', menu?.results["default"].next));
        }

        nodeText = this.getMultipleSelectDescription(menu, nodeText);
        break;

      case menuType.AGENTHANDOFF:
        nextSteps = Object.keys(menu.results).map(
          (result) => new NextStepDetails(result.toString(), menu.results[result].next)
        );
        nodeText = menu.touch.businessHours + newLine + menu.touch.touchOptions;
        break;
      case menuType.RICHLINK:
        nextSteps = [new NextStepDetails('next', step.next)];
        nodeText = menu.menuHeader + newLine + menu.url;
        break;
      case menuType.TIMEPICKER:
      case menuType.MULTI_PRODUCT:
        nextSteps = [new NextStepDetails('next', step.next)];
        nodeText = menu.menuHeader + newLine;
        break;
      case menuType.ORCA_PAYMENT:
        nextSteps = Object.keys(menu.results).map(
          (result) => new NextStepDetails(result.toString(), menu.results[result].next)
        );
        nodeText = `${menu.name} ${newLine}
                            ${menu.menuHeader} ${newLine}
                            ${this.getOrcaPaymentText(menu.menuParams['lineItems'])}`;
        break;
      case menuType.CHATDESK:
        nextSteps = Object.keys(menu.results).map(
          (result) => new NextStepDetails(result.toString(), menu.results[result].next)
        );
        nodeText = '';
        break;
      case menuType.OAUTH:
        nextSteps = Object.keys(menu.results).map((result) => {
          return new NextStepDetails(result.toString(), menu.results[result].next);
        });
        nodeText = menu.menuHeader;
        break;
      case menuType.IMESSAGE:
        nextSteps = [new NextStepDetails('next', step.next)];
        nodeText = menu.menuHeader + newLine;
        break;
      case menuType.CHAT2PAY:
        nextSteps = Object.keys(menu.results).map(result => new NextStepDetails(result.toString(), menu.results[result].next));
        nodeText = menu.payment.messageForLink;
        break;
      case menuType.DYNAMICLIST:
        if (menu.results.default && menu.results.default.next !== 'OPTIONAL') {
          nextSteps = [new NextStepDetails('success', menu.results.success.next),
            new NextStepDetails('error', menu.results.error.next),
            new NextStepDetails('default', menu.results.default.next)];
        } else {
          nextSteps = [new NextStepDetails('success', menu.results.success.next), new NextStepDetails('error', menu.results.error.next)];
        }
        nodeText = menu.menuHeader + newLine + menu.menuParams.groupTitle + newLine + step.menu['bodyText'];
        break;
      case menuType.CONTACTSENDING:
        nextSteps = [new NextStepDetails('next', step.next)];
        nodeText = menu.menuParams['contact']['name']['firstName'] + newLine + menu.menuParams['contact']['name']['lastName'] + newLine + menu.menuParams['contact']['name']['formattedName'];
        break;
      case menuType.CONTACTRECEIVING:
        nextSteps = [new NextStepDetails('next', step.next)];
        nodeText = menu.text + newLine;
        break;
      case menuType.MENUFORM:
        nextSteps = [new NextStepDetails('next', step.next)];
        nodeText = menu.menuHeader + newLine;
        break;
      case menuType.AIASSISTANT:
        nextSteps = Object.keys(menu.results).map(
            (result) => new NextStepDetails(result.toString(), menu.results[result].next)
        );
        nodeText = menu?.name ? menu?.name : ''
        break;
      default:
        console.error('could not resolve MenuType');
        break;
    }

    if (this.nodeInfo[currentMenuType]) {
      const canvas: Canvas = this.nodeInfo[currentMenuType].canvas;
      const title: string = this.nodeInfo[currentMenuType].title;
      const nodeDetails = new NodeDetails(nodeText, stepType, currentMenuType, stepId, canvas, title);
      return new FlowBuilder(nodeDetails, nextSteps);
    }
  }

  setMenuNext(step: Step, nextStepData: OperationState): Object {
    const tempStep = step;
    const currentMenuType: string = tempStep.menu.menuType
      ? tempStep.menu.menuType
      : getMenuType(tempStep);

    switch (currentMenuType) {
      case menuType.MULTIPLECHOICE:
        if (tempStep.next) {
          tempStep.next = this.setNextStepValue(tempStep.next, nextStepData, 'next');
        }
        if (tempStep?.menu?.results["default"]?.next && tempStep?.menu?.results["default"]?.next !== 'OPTIONAL') {
          tempStep.menu.results["default"].next = this.setNextStepValue(tempStep.menu.results["default"].next, nextStepData, 'default');
        }
        break;
      case menuType.CONTACTRECEIVING:
      case menuType.CONTACTSENDING:
      case menuType.RICHLINK:
        if (tempStep.next) {
          tempStep.next = this.setNextStepValue(tempStep.next, nextStepData, 'next');
        }
        break;
      case menuType.ASKINPUT:
      case menuType.MEDIA:
      case menuType.TIMEPICKER:
      case menuType.LOCATION:
      case menuType.IMESSAGE:
      case menuType.MULTI_PRODUCT:
        if (tempStep.next) {
          tempStep.next = this.setNextStepValue(tempStep.next, nextStepData, 'input');
        }
        break;
      case menuType.DYNAMICLIST:
      case menuType.EXTERNALLINK:
      case menuType.PAYMENT:
      case menuType.CHATDESK:
      case menuType.OTPMENU:
      case menuType.AGENTHANDOFF:
      case menuType.ORCA_PAYMENT:
      case menuType.OAUTH:
      case menuType.CHAT2PAY:
      case menuType.AIASSISTANT:
        Object.keys(tempStep.menu.results).map((result) => {
          tempStep.menu.results[result].next = this.setNextStepValue(
            tempStep.menu.results[result].next,
            nextStepData,
            result
          );
        });
        break;
      case menuType.MENUOPTION:
        Object.keys(tempStep.menu.options.items).map((items) => {
          tempStep.menu.options.items[items].next = this.setNextStepValue(
            tempStep.menu.options.items[items].next,
            nextStepData,
            items
          );
        });
        if (tempStep?.menu.results?.default && tempStep?.menu.results?.default?.next !== 'OPTIONAL') {
          tempStep.menu.results.default.next = this.setNextStepValue(
              tempStep?.menu.results?.default?.next,
              nextStepData,
              'default'
          );
        }
        break;
      case menuType.DISPLAYMENU:
        const endSession =
          tempStep.menu &&
          tempStep.menu.menuParams &&
          tempStep.menu.menuParams.hasOwnProperty('endSession');
        if (tempStep.next && endSession) {
          tempStep.next = this.setNextStepValue(tempStep.next, nextStepData, '');
        }
        break;
      case menuType.MENUFORM:
        if (tempStep.next) {
          tempStep.next = this.setNextStepValue(tempStep.next, nextStepData, 'next');
        }
        break;
      // case menuType.CHATDESK:
      //     Object.keys(tempStep.menu.results).map(result => {
      //         tempStep.menu.results[result].next = this.setNextStepValue(tempStep.menu.results[result].next, nextStepData, result);
      //     });
      //     break;
      default:
        return tempStep;
    }

    return tempStep;
  }

  setStartNext(step: Step, nextStepData: OperationState) {
    step.next = this.setNextStepValue(step.next, nextStepData, '');
    return step;
  }

  setIntegrationNext(step: Step) {
    if (step.next) {
      step.next = this.setNextStepValue(step.next, null, '');
      return step;
    }
    return step;
  }

  setNextStepValue(currentValue: string, nextStepData: OperationState, resultName: string) {
    if (currentValue === newStepValue && !nextStepData) {
      return this.stepNameGenerator();
    } else if (nextStepData && nextStepData.data) {
      if (
        nextStepData.data.all ||
        (nextStepData.data.from && (<string[]>nextStepData.data.from).includes(resultName))
      ) {
        return this.stepNameGenerator();
      } else if (nextStepData.data.to && (<string[]>nextStepData.data.to).includes(resultName)) {
        return nextStepData.data.value;
      }
    }
    return currentValue;
  }

  getMenuOptionDescription(menu: Menu, nodeText: string) {
    if (menu.menuHeader) {
      nodeText = menu.menuHeader + newLine;
    }
    const menuOption = menu.options;
    const menuItems = Object.keys(menuOption.items);
    nodeText = this.getMenuItemsText(menuOption, nodeText, menuItems);
    return nodeText;
  }

  getMultipleSelectDescription(menu: Menu, nodeText: string) {
    if (menu.menuHeader) {
      nodeText = menu.menuHeader + newLine;
    }
    const menuOption = menu.options;
    const menuGroups = menu.groups;

    if (menuOption) {
      const menuItems = Object.keys(menuOption.items);
      nodeText = this.getMenuItemsText(menuOption, nodeText, menuItems);
    }
    if (menuGroups) {
      for (const group in menuGroups) {
        const groupItems = menuGroups[group].items;
        if (groupItems) {
          map(groupItems, (item) => {
            nodeText = `${nodeText ? nodeText : ''}
                        ${item && item.text ? item.text : ''}
                        ${newLine}`;
          });
        }
      }
    }

    return nodeText;
  }

  getMenuItemsText(menuOption: any, menuText: string, menuItems: Array<string>) {
    menuItems.forEach((item) => {
      menuText = `${menuText ? menuText : ''}
                        ${item ? item : ''}
                        ${menuOption.optionSeperator ? menuOption.optionSeperator : '. '}
                        ${menuOption.items[item].text ? menuOption.items[item].text : ''}
                        ${newLine}`;
    });
    return menuText;
  }

  getOrcaPaymentText(lineItems: {}) {
    let menuText = '';
    for (const lineItem in lineItems) {
      if (lineItems[lineItem]['label'] && lineItems[lineItem]['amount']) {
        menuText += `${lineItems[lineItem]['label']} : ${lineItems[lineItem]['amount']} ${newLine}`;
      }
    }

    return menuText;
  }

  stepNameGenerator() {
    const stepList = this.currentDisplayedSteps.concat(this.currentDeletedStepsId);
    if (stepList.length === 0) {
      this.getAllSteps.push('step1');
      return 'step1';
    }
    let temp = 0;
    stepList.forEach((element) => {
      const stepName: string = element;
      const stepNumber: number = parseInt(stepName.substring(4, stepName.length), 10);
      if (stepNumber > temp) {
        temp = stepNumber;
      }
    });
    temp += 1;
    const newTempStep = 'step' + temp;
    this.addStepToCurrentDisplayed(newTempStep);
    return newTempStep;
  }

  /**
   * Gets the last step number in flow
   */
  getLastStepNumber() {
    const stepList = this.currentDisplayedSteps.concat(this.currentDeletedStepsId);
    if (stepList.length === 0) {
      return 1;
    }

    return  stepList.map(stepName => {
      return parseInt(stepName.substring(4, stepName.length), 10);
    }).reduce((previousValue, currentValue) => {
      if (currentValue > previousValue) {
        return currentValue;
      }

      return previousValue;
    }, 0);
  }

  deleteStep(deleteStep: DeleteStepData): void {
    const step = cloneDeep(this.flow[deleteStep.parentId]);
    this.operationData = {
      id: deleteStep.stepId,
      parentId: deleteStep.parentId,
      data: {
        from: this.getNextStepId(step, deleteStep.parentId, deleteStep.stepId),
        value: deleteStep.stepId,
      },
      operationType: 'DELETE',
    };

    const from = this.operationData.parentId;
    const from_step = { ...this.flow[from] };

    this.flow[from] = {
      ...this.setNextStep(from_step, this.operationData),
    };
    this.operationData.data.from = [];

    this.operationData = null;
    this.buildFlow(this.flow);
  }

  clearSteps(steps: string[]): void {
    for (const step of steps) {
      delete this.flow[step];
      this.buildFlow(this.flow);
    }
  }

  cut(stepId: string, parentId: string): void {
    this.operationData = null;
    const step = cloneDeep(this.flow[parentId]);
    this.operationData = {
      id: stepId,
      parentId: parentId,
      data: {
        from: this.getNextStepId(step, parentId, stepId),
        value: stepId,
      },
      operationType: 'CUT',
    };
  }

  copy(stepId: string, parentId: string): void {
    this.operationData = null;
    this.copiedStep = cloneDeep(this.flow[stepId]);
    if(this.copiedStep['metadata']) {
      this.copiedStep['metadata'].stepUniqueID = "NEW";
    }
    this.operationData = {
      id: stepId,
      data: {
        from: this.getNextStepId(this.copiedStep, stepId),
        value: '',
      },
      operationType: 'COPY',
    };
  }

  paste(stepId: string, parentId: string): void {
    document.getElementById(`node-${stepId}`).style.backgroundImage = '';
    if (this.operationData) {
      switch (this.operationData.operationType) {
        case 'COPY':
          this.flow[stepId] = cloneDeep(
            this.setNextStep(this.copiedStep, this.operationData)
          );
          break;
        case 'CUT':
          this.pastCutFrom();
          this.pastCutTo(parentId, stepId);
          this.operationData = null;
          break;
      }
      this.buildFlow(this.flow);
    }
  }

  restoreStep(stepId: string, parentId: string, filter: string, restoreChildren: boolean): void {
    const step = cloneDeep(this.flow[parentId]);
    this.operationData = {
      id: stepId,
      parentId: parentId,
      data: {
        to: this.getNextStepId(step, parentId, filter),
        value: stepId,
      },
      operationType: 'RESTORE',
    };

    const from = this.operationData.parentId;
    const from_step = { ...this.flow[from] };

    this.addStepToCurrentDisplayed(from);
    this.flow[from] = {
      ...this.setNextStep(from_step, this.operationData),
    };
    this.operationData.data.from = [];

    this.operationData = null;

    if (!restoreChildren) {
      this.restoreStepOnly(stepId);
      return;
    }

    this.buildFlow(this.flow);
  }

  restoreStepOnly(stepId: string): void {
    this.operationData = {
      id: stepId,
      parentId: stepId,
      data: {
        all: true,
      },
      operationType: 'RESTORE',
    };

    const from = this.operationData.parentId;
    const from_step = { ...this.flow[from] };

    this.addStepToCurrentDisplayed(from)

    this.flow[from] = {
      ...this.setNextStep(from_step, this.operationData),
    };
    setTimeout(() => {
      this.buildFlow(this.flow);
    }, 0);
  }

  private pastCutFrom() {
    const from = this.operationData.parentId;
    const from_step = this.flow[from];
    this.flow[from] = this.setNextStep(from_step, this.operationData);
    this.operationData.data.from = [];
  }

  private pastCutTo(parentId: string, stepId: string) {
    const to = parentId;
    const toStep = this.flow[to];
    this.operationData.data.to = this.getNextStepId(toStep, to, stepId);

    this.flow[to] = this.setNextStep(toStep, this.operationData);
  }

  private getNextStepId(step, stepId, filterOn?: string) {
    return this.getNodeDetails(step, stepId)
      .nextSteps.filter((results) => {
        if (filterOn !== null && filterOn !== undefined) {
          return results.nextStepId === filterOn;
        } else {
          return true;
        }
      })
      .map((result) => {
        const indexOfColon = result.description.indexOf(':');
        if (indexOfColon !== -1) {
          return result.description.slice(0, indexOfColon);
        }
        return result.description;
      });
  }

  startNodeConfig(): Step {
    const start: Step = {
      type: 'start',
      next: startStep(this.flow),
    };

    return start;
  }

  checkLoop(stepNumber: string): boolean {
    const loop = find(this.loops, (o) => o === stepNumber);
    return !!loop;
  }

  getCurrFlow() {
    return this.currFlow;
  }

  setCurrFlow(flow) {
    this.currFlow = flow;
  }

  getFlow() {
    return this.flow;
  }

  getModalOpen() {
    return this.modalOpen;
  }

  setModalOpen(status) {
    this.modalOpen = status;
  }

  addStep(nodeId: string, step: Step) {
    this.flow[nodeId] = this.setNextStep(step)
    this.rebuildFlow();
  }

  canSave(): boolean {
    if (!this.flow && !this.flow['start']) {
      return false;
    }

    const startNext = this.flow['start']['next'];
    const firstStep = this.flow[startNext];
    const moreThanTwoSteps = Object.keys(this.flow).length >= 2; // start will always make it 1 and step x will be 2 meaning there is a step added

    const isFirstStepNotNew = !(firstStep
      ? firstStep.type === 'new'
      : false); // getting the 'first' step based on what is set in the start step.
    return !(moreThanTwoSteps && isFirstStepNotNew);
  }

  get hasInvalidSteps(): { valid: boolean, steps: [] } {
    const steps = _filter(this.flow, (o, i) => {
      const deletedStep = find(this.getDeletedSteps, (item) => {
        return item.step.stepNumber === i;
      });
      o.step = i;
      return o.valid === false && !deletedStep;
    }).map((o) => {
      return o.step;
    });
    const valid = (steps.length !== 0) || (Object.keys(this.flow).length < 2)
    return { valid, steps }
  }

  private addStepToCurrentDisplayed(stepId: string) {
    if (!this.currentDisplayedSteps.includes(stepId)) {
      this.currentDisplayedSteps.push(stepId);
    }
  }

  setUnexecutedStyles() {
    this.jsPlumbService.setUnexecutedStyles();
  }

  setExecutedStyles(source: string, target: string) {
    this.jsPlumbService.setExecutedStyles(source, target);
  }

  setDefaultStyles() {
    this.jsPlumbService.redrawArrows();
  }
}

function getStepType(step: Step): FlowStepTypes {
  return step ? (step.type as FlowStepTypes) : flowStepType.NEW;
}

function getStepNumber(nodeId: string, lastIndexOf: string = '_') {
  let stepNumber: string = nodeId;
  if (nodeId.indexOf(lastIndexOf) !== -1) {
    const lastIndex: number = nodeId.lastIndexOf(lastIndexOf) + 1;
    stepNumber = nodeId.substr(lastIndex);
  }
  return stepNumber;
}

function idGenerator(parentStep: string, result: string | number, destinationStep: string) {
  return ((parentStep + '_' + result) as string) + '_' + destinationStep;
}

function isFlowId(stepId: string) {
  if (!stepId) {
    return false;
  }
  return stepId.indexOf('step') !== -1 && stepId.indexOf(':') === -1;
}

function getMenuType(step: Step): MenuTypes {
  const menu = step.menu;

  if (menu.text && !menu.results) {
    if (step.next) {
      return menuType.ASKINPUT;
    } else {
      return menuType.DISPLAYMENU;
    }
  } else if (menu.results) {
    if (menu.payment) {
      return menuType.PAYMENT;
    } else {
      return menuType.EXTERNALLINK;
    }
  } else if (menu.options) {
    if (step.next !== endStepValue) {
      return menuType.MULTIPLECHOICE;
    } else {
      return menuType.MENUOPTION;
    }
  }

  return null;
}

function calculatorDeletedSteps(
  flow: any,
  currentDisplayedSteps: string[]
): { [index: string]: Step } {
  const localDeletedSteps = {};
  for (const stepId in flow) {
    if (!currentDisplayedSteps.includes(stepId)) {
      localDeletedSteps[stepId] = flow[stepId];
    }
  }

  return localDeletedSteps;
}

function alphabeticallyOrderMap(a, b): any {
  const nameA = a.toUpperCase(); // ignore upper and lowercase
  const nameB = b.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }

  // names must be equal
  return 0;
}

function startStep(flow: Step) {
  const jsonDataKey = Object.keys(flow);
  if (jsonDataKey.length > 0) {
    let tempNumber = parseInt(jsonDataKey[0].substring(4, jsonDataKey[0].length), 10);

    jsonDataKey.forEach((element) => {
      const stepNumber: number = parseInt(element.substring(4, element.length), 10);
      if (tempNumber > stepNumber) {
        tempNumber = stepNumber;
      }
    });
    return 'step' + tempNumber;
  } else {
    return 'step1';
  }
}

function isStartStep(stepId: string) {
  return stepId === 'start';
}

const getDisplayType = (nodeType: FlowStepTypes) => {
  switch (nodeType) {
    case "new":
    case "start":
    case "flow":
      return nodeType
    default:
      return "node"
  }
}
